import React, { useEffect, useState, useCallback } from "react";
import { Smartphone, Banknote, Phone, QrCode, Trash2, Check, Plus, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

export const METHOD_META = { upi: ["UPI ID", Smartphone], bank: ["Bank A/c", Banknote], gpay: ["GPay Number", Phone], qr: ["QR Code", QrCode] };

export function useSavedMethods() {
  const [methods, setMethods] = useState([]);
  const reload = useCallback(() => api.get("/payout-methods").then((r) => setMethods(r.data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);
  const remove = async (id) => {
    try { await api.delete(`/payout-methods/${id}`); toast.success("Saved method removed"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setDefault = async (id) => {
    try { await api.put(`/payout-methods/${id}/default`); toast.success("Default payout method set"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const defaultMethod = methods.find((m) => m.is_default) || null;
  return { methods, reload, remove, setDefault, defaultMethod };
}

// One-tap chips (withdrawal page) or full list with delete (Me page).
export function SavedMethods({ methods, selectedId, onPick, onRemove, onSetDefault, manage = false }) {
  const navigate = useNavigate();
  if (!methods.length && !manage) return null;
  return (
    <div data-testid="saved-methods">
      {!manage && <div className="text-xs text-slate-500 mb-1">Saved payout methods · tap to use</div>}
      <div className={manage ? "space-y-2" : "flex gap-2 overflow-x-auto pb-1 -mx-1 px-1"}>
        {methods.map((m) => {
          const [label, Icon] = METHOD_META[m.method] || METHOD_META.upi;
          const active = selectedId === m.id;
          return (
            <div key={m.id} role={onPick ? "button" : undefined} tabIndex={onPick ? 0 : undefined} onClick={() => onPick?.(m)} onKeyDown={(e) => e.key === "Enter" && onPick?.(m)} data-testid={`saved-method-${m.id}`}
              className={`rl-card flex items-center gap-2 ${manage ? "p-3" : "px-3 py-2 shrink-0 max-w-[230px] cursor-pointer"} ${active ? "ring-2 ring-amber-400" : ""}`}>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${active ? "rl-gold-bg" : "bg-slate-100 text-slate-600"}`}>{active ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}</div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] text-slate-500 flex items-center gap-1">{label}{m.is_default && <span className="text-[9px] font-black text-amber-700 bg-amber-100 rounded px-1" data-testid={`saved-method-default-${m.id}`}>DEFAULT</span>}</div>
                <div className="text-xs font-bold text-slate-900 truncate font-mono" data-testid={`saved-method-label-${m.id}`}>{m.label}</div>
              </div>
              {onSetDefault && <button onClick={(e) => { e.stopPropagation(); onSetDefault(m.id); }} className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${m.is_default ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-400"}`} aria-label="set default" data-testid={`saved-method-star-${m.id}`}><Star className={`w-4 h-4 ${m.is_default ? "fill-amber-400" : ""}`} /></button>}
              {onRemove && <button onClick={(e) => { e.stopPropagation(); onRemove(m.id); }} className="w-8 h-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center shrink-0" aria-label="remove" data-testid={`saved-method-remove-${m.id}`}><Trash2 className="w-4 h-4" /></button>}
            </div>
          );
        })}
        {manage && (
          <button onClick={() => navigate("/withdraw")} className="w-full rl-card p-3 flex items-center justify-center gap-2 text-sm font-semibold text-blue-600 border-2 border-dashed border-blue-200" data-testid="saved-method-add">
            <Plus className="w-4 h-4" />{methods.length ? "Add another payout method" : "Add a payout method (UPI · Bank · GPay · QR)"}
          </button>
        )}
      </div>
    </div>
  );
}
